import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { DateInput } from '@/components/form/date-input';
import { Text } from '@/components/ui';
import { Modal } from '@/components/ui/modal';
import { useInvalidateAccounting } from '@/hooks/use-accounting';
import { accountingService } from '@/services/accounting-service';
import { ImportBatchKind } from '@/types/models';
import { identityIndexOf, resolveStartingBalanceRow } from '@/utils/account-identity';
import { dollarsToCents, pickAndParseFiles } from '@/utils/accounting-export';
import { classifyTemplateCsv, CSV_TEMPLATES, downloadCsvTemplate, normalizeRow, parsePercentToBps } from '@/utils/csv-templates';
import { toBusinessMs, todayBusinessDate } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';
import { formatParPercent } from '@/utils/par-value';

interface Props {
	associationId: string;
	onClose: () => void;
}

type Phase = 'pick' | 'matching' | 'preview' | 'importing' | 'done';

/** A per-owner opening balance from the customer-balances template CSV. */
interface ArDraft {
	displayName: string;
	netCents: number;
	/**
	 * Ownership share in bps from `pct_ownership` (#464). Held on the draft
	 * itself rather than inside `toCreate`, because rows matching an EXISTING
	 * account have no `toCreate` — nesting it there would silently drop the
	 * percentage for every owner already on the roster.
	 */
	parValueBps?: number;
	/** Resolved existing account. */
	accountId?: string;
	/** Unmatched rows with enough contact fields are created at commit time. */
	toCreate?: {
		accountNumber: string;
		address?: string;
		lotNumber?: string;
		firstName?: string;
		lastName?: string;
		email?: string;
		phone?: string;
	};
}

/**
 * Fresh-takeover importer. One CSV — the customer-balances template — does two
 * jobs at once: it creates the customer accounts (every row, including owners
 * at a zero balance, so the whole roster lands) and posts what each owner owes
 * (positive → opening charge) or has prepaid (negative → opening credit) as
 * Starting Balances against the default GL chart.
 *
 * The default chart is seeded automatically before posting (idempotent), so a
 * brand-new association doesn't need a separately-configured Accounts Receivable
 * GL code first.
 */
export const ImportStartingBalancesModal: React.FC<Props> = ({ associationId, onClose }) => {
	const [phase, setPhase] = useState<Phase>('pick');
	const [arDrafts, setArDrafts] = useState<ArDraft[]>([]);
	const [unmatched, setUnmatched] = useState<Array<{ name: string; reason: string }>>([]);
	const [fileSummary, setFileSummary] = useState<Array<{ name: string; label: string; ok: boolean }>>([]);
	const [parseErrors, setParseErrors] = useState<string[]>([]);
	const [asOf, setAsOf] = useState<Date | null>(todayBusinessDate);
	const [progressLabel, setProgressLabel] = useState('');
	const [result, setResult] = useState({ posted: 0, skipped: 0, accountsCreated: 0, failed: 0, reconciledCents: 0, parValueSet: 0 });
	const [importErrors, setImportErrors] = useState<string[]>([]);

	const invalidate = useInvalidateAccounting();

	const arNonZero = useMemo(() => arDrafts.filter((d) => d.netCents !== 0), [arDrafts]);
	const arZero = arDrafts.length - arNonZero.length;
	// Every unmatched row carrying contact fields is created — zero-balance
	// owners included, so the full roster lands in Customer Accounts.
	const toCreateCount = useMemo(() => arDrafts.filter((d) => !d.accountId && d.toCreate).length, [arDrafts]);

	// Par value is a declared legal share, so the roster should total 100%. It
	// rarely lands there exactly (the declaration itself is rounded), so this is
	// surfaced as a warning and never blocks the import.
	const parValue = useMemo(() => {
		const withShare = arDrafts.filter((d) => d.parValueBps != null);
		const totalBps = withShare.reduce((s, d) => s + (d.parValueBps ?? 0), 0);
		return { count: withShare.length, totalBps, offBy: totalBps - 10_000 };
	}, [arDrafts]);
	const parValueOff = parValue.count > 0 && Math.abs(parValue.offBy) > 1;

	const totals = useMemo(() => {
		let owed = 0, prepaid = 0, owedCount = 0, prepaidCount = 0;
		for (const { netCents } of arNonZero) {
			if (netCents > 0) { owed += netCents; owedCount++; }
			else { prepaid += netCents; prepaidCount++; }
		}
		// Net = what the file says the association's total AR is at cut-over.
		return { owed, prepaid, owedCount, prepaidCount, net: owed + prepaid };
	}, [arNonZero]);

	const pick = async () => {
		const files = await pickAndParseFiles();
		if (files.length === 0) return;
		setPhase('matching');

		const accountsResp = await accountingService.listAccounts({ associationId, includeArchived: true, limit: 2000 });
		const accounts = accountsResp.data ?? [];
		// The shared identity rule (utils/account-identity): a number only matches when the
		// account's stored QuickBooks owner agrees with the row's name. After a #645 renumber
		// "5977AS" belongs to the live owner; a balance row naming the builder is reported,
		// never posted onto the owner.
		const identity = identityIndexOf(accounts);

		const nextAr: ArDraft[] = [];
		const nextUnmatched: Array<{ name: string; reason: string }> = [];
		const errs: string[] = [];
		const summary: Array<{ name: string; label: string; ok: boolean }> = [];

		for (const f of files) {
			const kind = classifyTemplateCsv(f.rows);
			if (kind === 'customer-balances') {
				summary.push({ name: f.name, label: `Customer balances · ${f.rows.length} rows`, ok: true });
				for (const raw of f.rows) {
					const r = normalizeRow(raw);
					if (!r.account_number) continue;
					// Balance is optional — a blank or missing value means a
					// roster-only row (account created, no opening balance).
					const cents = r.balance ? dollarsToCents(r.balance) : 0;
					if (cents == null) {
						errs.push(`${f.name}: account ${r.account_number} has an unreadable balance "${r.balance}"`);
						continue;
					}
					const displayName = [r.first_name, r.last_name].filter(Boolean).join(' ') || r.account_number;
					// pct_ownership is optional; an unparseable value is a warning, not a
					// dropped row — the balance still needs to import.
					const parValueBps = parsePercentToBps(r.pct_ownership) ?? undefined;
					if (r.pct_ownership && parValueBps == null) {
						errs.push(`${f.name}: account ${r.account_number} has an unreadable ownership percent "${r.pct_ownership}"`);
					}
					const who = resolveStartingBalanceRow(identity, {
						accountNumber: r.account_number, firstName: r.first_name, lastName: r.last_name,
					});
					if (who.kind === 'conflict') {
						nextUnmatched.push({ name: r.account_number, reason: who.reason });
					} else if (who.kind === 'match') {
						nextAr.push({ displayName: `${r.account_number} — ${displayName}`, netCents: cents, parValueBps, accountId: who.accountId });
					} else if (r.first_name || r.last_name || r.address) {
						nextAr.push({
							displayName: `${r.account_number} — ${displayName}`,
							netCents: cents,
							parValueBps,
							toCreate: {
								accountNumber: r.account_number,
								address: r.address || undefined,
								lotNumber: r.lot_number || undefined,
								firstName: r.first_name || undefined,
								lastName: r.last_name || undefined,
								email: r.email || undefined,
								phone: r.phone || undefined,
							},
						});
					} else {
						nextUnmatched.push({ name: r.account_number, reason: 'no matching account; add a name or address column to create it' });
					}
				}
			} else if (kind !== null) {
				summary.push({ name: f.name, label: `${CSV_TEMPLATES[kind].title} — not the customer balances template`, ok: false });
			} else {
				summary.push({ name: f.name, label: 'not recognized — download the template below', ok: false });
			}
		}

		setArDrafts(nextAr);
		setUnmatched(nextUnmatched);
		setParseErrors(errs);
		setFileSummary(summary);
		setPhase('preview');
	};

	const runImport = async () => {
		if (!asOf) return;
		setPhase('importing');
		// Snap to the canonical midnight-UTC business-date grid — a raw
		// `.getTime()` persists an off-grid instant into charges.posted_on /
		// credits.issued_on, which the ledger's `<= todayEtMs()` bound excludes.
		const asOfMs = toBusinessMs(asOf);
		const errors: string[] = [];
		let accountsCreated = 0;

		// The GL chart is global and always present; the association's AR control
		// pointer is wired at creation / by the global-chart migration, so opening
		// charges below have a control account to post against.
		let batchId: string | undefined;
		try {
			const batch = await accountingService.createImportBatch({
				kind: ImportBatchKind.StartingBalances,
				associationId,
				sourceFilename: fileSummary.filter((f) => f.ok).map((f) => f.name).join(', ') || 'Starting balances CSV',
			});
			batchId = batch.id;
		} catch (e) {
			errors.push(`Failed to create import batch (revert won't be available): ${(e as Error).message}`);
		}

		// 1. Create accounts for every unmatched row that carries contact fields
		//    — including zero-balance rows — then re-list to resolve their ids by
		//    account number.
		const drafts = arDrafts.map((d) => ({ ...d }));
		const toCreate = drafts.filter((d) => !d.accountId && d.toCreate);
		if (toCreate.length > 0) {
			const CHUNK = 200;
			for (let start = 0; start < toCreate.length; start += CHUNK) {
				setProgressLabel(`Creating accounts ${Math.min(start + CHUNK, toCreate.length)}/${toCreate.length}…`);
				try {
					const r = await accountingService.bulkImportCustomers({
						associationId,
						rows: toCreate.slice(start, start + CHUNK).map((d) => ({ ...d.toCreate!, parValueBps: d.parValueBps })),
						importBatchId: batchId,
					});
					accountsCreated += r.accountsCreated;
				} catch (e) {
					errors.push(`Account-creation chunk failed (may have partially saved — re-running is a no-op): ${(e as Error).message}`);
					break;
				}
			}
			try {
				const fresh = await accountingService.listAccounts({ associationId, includeArchived: true, limit: 2000 });
				const freshByNumber = new Map((fresh.data ?? []).filter((a) => a.accountNumber).map((a) => [a.accountNumber!.trim(), a.id]));
				for (const d of toCreate) {
					d.accountId = freshByNumber.get(d.toCreate!.accountNumber.trim());
					if (!d.accountId && d.netCents !== 0) errors.push(`Account ${d.toCreate!.accountNumber} was not created — its balance was skipped`);
				}
			} catch (e) {
				errors.push(`Could not re-load accounts after creation: ${(e as Error).message}`);
			}
		}

		// 2. Write the ownership shares for rows that matched an account which
		//    already existed (#464). Newly-created accounts got theirs in the
		//    INSERT above; these can't go through /customers/bulk without also
		//    overwriting their address, so they use the narrow endpoint.
		const parItems = drafts
			.filter((d) => d.accountId && d.parValueBps != null && !toCreate.includes(d))
			.map((d) => ({ accountId: d.accountId!, parValueBps: d.parValueBps! }));
		let parValueUpdated = 0;
		const PAR_CHUNK = 500;
		for (let start = 0; start < parItems.length; start += PAR_CHUNK) {
			setProgressLabel(`Setting ownership percentages ${Math.min(start + PAR_CHUNK, parItems.length)}/${parItems.length}…`);
			try {
				const r = await accountingService.bulkSetParValue({
					associationId, importBatchId: batchId, items: parItems.slice(start, start + PAR_CHUNK),
				});
				parValueUpdated += r.updated;
				for (const e of r.errors) errors.push(`ownership % for account ${e.accountId}: ${e.message}`);
			} catch (e) {
				errors.push(`Ownership-percentage chunk failed (safe to re-run): ${(e as Error).message}`);
				break;
			}
		}

		// 3. Post opening balances for the non-zero rows in short chunked POSTs
		//    per the mandatory bulk pattern. Zero-balance rows are roster-only —
		//    their account now exists, nothing to post.
		const items = drafts.filter((d) => d.accountId && d.netCents !== 0).map((d) => ({ accountId: d.accountId!, balanceCents: d.netCents }));
		const CHUNK_SIZE = 100;
		let posted = 0, skipped = 0, failed = 0;
		for (let start = 0; start < items.length; start += CHUNK_SIZE) {
			const chunk = items.slice(start, start + CHUNK_SIZE);
			setProgressLabel(`Posting starting balances ${Math.min(start + CHUNK_SIZE, items.length)}/${items.length}…`);
			try {
				const r = await accountingService.bulkStartingBalances({
					associationId, importBatchId: batchId, asOfMs, items: chunk, glItems: [],
				});
				posted += r.posted;
				skipped += r.skipped;
				failed += r.errors.length;
				for (const e of r.errors) errors.push(`${e.accountId ? `account ${e.accountId}` : `GL ${e.glCode}`}: ${e.message}`);
			} catch (e) {
				const msg = (e as Error)?.message ?? String(e);
				errors.push(`A chunk failed (may have partially saved — refresh the Batches list): ${msg}`);
				failed += chunk.length;
				break;
			}
		}

		await invalidate();
		// Newly-created accounts got their share in the INSERT; parValueUpdated
		// only counts the ones patched afterwards. Count only rows whose account
		// actually exists now (accountId was resolved from the re-list above) —
		// otherwise a failed creation is still reported as a share we set.
		const parValueSet = parValueUpdated
			+ toCreate.filter((d) => d.parValueBps != null && d.accountId).length;
		setResult({ posted, skipped, accountsCreated, failed, reconciledCents: totals.net, parValueSet });
		setImportErrors(errors);
		setPhase('done');
	};

	const hasAnything = arDrafts.length > 0;

	return (
		<Modal visible onDismiss={onClose} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>Import Customers &amp; Starting Balances</Text>
				<Pressable onPress={onClose} hitSlop={10}><Text style={styles.close}>✕</Text></Pressable>
			</View>
			<ScrollView contentContainerStyle={styles.body}>
				<Text style={styles.hint}>
					One CSV sets up the whole association: every row becomes a customer account, and its balance — dated at
					your cut-over date — becomes an opening charge (owed) or credit (prepaid). Leave the balance blank or 0
					to add an owner to the roster without an opening balance. The default GL chart is set up automatically.
				</Text>

				<View style={styles.templatesBox}>
					<Text style={styles.templatesTitle}>CSV template (with sample rows)</Text>
					<Pressable style={styles.templateRow} onPress={() => downloadCsvTemplate('customer-balances')}>
						<Ionicons name="download-outline" size={16} color="#1976d2" />
						<View style={{ flex: 1, minWidth: 0 }}>
							<Text style={styles.templateName}>{CSV_TEMPLATES['customer-balances'].title} — {CSV_TEMPLATES['customer-balances'].filename}</Text>
							<Text style={styles.templateDesc}>{CSV_TEMPLATES['customer-balances'].description}</Text>
						</View>
					</Pressable>
				</View>

				{phase === 'pick' && (
					<Pressable style={styles.primary} onPress={pick}>
						<Text style={styles.primaryLabel}>Pick CSV file…</Text>
					</Pressable>
				)}

				{phase === 'matching' && <Text style={styles.note}>Parsing + matching accounts…</Text>}

				{(phase === 'preview' || phase === 'done') && (
					<View style={styles.previewBox}>
						{fileSummary.map((f, i) => (
							<PreviewRow key={i} label={f.name} value={f.label} tone={f.ok ? undefined : 'red'} />
						))}
						<View style={styles.previewDivider} />
						<PreviewRow label="Accounts owing (→ charge)" value={`${totals.owedCount} · ${formatMoneyCents(totals.owed)}`} />
						<PreviewRow label="Accounts prepaid (→ credit)" value={`${totals.prepaidCount} · ${formatMoneyCents(totals.prepaid)}`} tone="green" />
						<PreviewRow label="Net AR at cut-over" value={formatMoneyCents(totals.net)} />
						{parValue.count > 0 && (
							<>
								<PreviewRow label="Accounts with ownership %" value={String(parValue.count)} />
								<PreviewRow
									label="Ownership total"
									value={formatParPercent(parValue.totalBps)}
									tone={parValueOff ? 'red' : undefined}
								/>
							</>
						)}
						{toCreateCount > 0 && <PreviewRow label="New accounts to create" value={String(toCreateCount)} />}
						{arZero > 0 && <PreviewRow label="Zero balance (roster only)" value={String(arZero)} />}
						{unmatched.length > 0 && <PreviewRow label="Unmatched rows" value={String(unmatched.length)} tone="red" />}
						{parseErrors.length > 0 && <PreviewRow label="Parse warnings" value={String(parseErrors.length)} tone="red" />}
					</View>
				)}

				{phase === 'preview' && unmatched.length > 0 && (
					<View style={styles.warnBox}>
						<Text style={styles.warnTitle}>Unmatched rows</Text>
						{unmatched.slice(0, 8).map((u, i) => (
							<Text key={i} style={styles.warnLine}>&ldquo;{u.name}&rdquo; — {u.reason}</Text>
						))}
						{unmatched.length > 8 && <Text style={styles.warnLine}>…and {unmatched.length - 8} more</Text>}
						<Text style={styles.warnNote}>
							These won&apos;t be imported. Add a name or address column so they can be created, then re-run —
							this importer is idempotent.
						</Text>
					</View>
				)}

				{phase === 'preview' && parValueOff && (
					<View style={styles.warnBox}>
						<Text style={styles.warnTitle}>Ownership percentages don&apos;t total 100%</Text>
						<Text style={styles.warnLine}>
							The {parValue.count} row{parValue.count === 1 ? '' : 's'} with a percentage add up to{' '}
							{formatParPercent(parValue.totalBps)} — {parValue.offBy > 0 ? 'over' : 'under'} by{' '}
							{formatParPercent(Math.abs(parValue.offBy))}.
						</Text>
						<Text style={styles.warnNote}>
							Importing anyway. Declared shares are often rounded, so a small gap is normal — but a large
							one usually means rows are missing from the file. Check the Par Value report after importing.
						</Text>
					</View>
				)}

				{phase === 'preview' && parseErrors.length > 0 && (
					<View style={styles.warnBox}>
						{parseErrors.slice(0, 8).map((e, i) => <Text key={i} style={styles.warnLine}>{e}</Text>)}
						{parseErrors.length > 8 && <Text style={styles.warnLine}>…and {parseErrors.length - 8} more</Text>}
					</View>
				)}

				{phase === 'preview' && (
					<>
						<DateInput
							label="Cut-over date (opening balances dated here)"
							value={asOf}
							onChange={setAsOf}
						/>
						<View style={{ flexDirection: 'row', gap: 8 }}>
							<Pressable style={styles.secondary} onPress={() => { setArDrafts([]); setUnmatched([]); setFileSummary([]); setParseErrors([]); setPhase('pick'); }}>
								<Text style={styles.secondaryLabel}>Reset</Text>
							</Pressable>
							<Pressable
								style={[styles.primary, { flex: 1, opacity: !hasAnything || !asOf ? 0.5 : 1 }]}
								onPress={runImport}
								disabled={!hasAnything || !asOf}
							>
								<Text style={styles.primaryLabel}>
									Import {arDrafts.length} customer{arDrafts.length === 1 ? '' : 's'}
								</Text>
							</Pressable>
						</View>
					</>
				)}

				{phase === 'importing' && (
					<View style={styles.resultBox}>
						<Text style={styles.resultOk}>{progressLabel || 'Importing…'}</Text>
					</View>
				)}

				{phase === 'done' && (
					<>
						<View style={styles.resultBox}>
							<Text style={styles.resultOk}>✓ {result.posted} opening balance{result.posted === 1 ? '' : 's'} posted</Text>
							{result.accountsCreated > 0 && <Text style={styles.resultLine}>{result.accountsCreated} customer accounts created</Text>}
							{result.parValueSet > 0 && <Text style={styles.resultLine}>{result.parValueSet} ownership percentages set</Text>}
							{result.skipped > 0 && (
								<Text style={styles.resultSkip}>{result.skipped} already had an opening balance (skipped)</Text>
							)}
							{result.failed > 0 && <Text style={styles.resultSkip}>{result.failed} failed</Text>}
							<Text style={styles.note}>
								Reconcile: this import totals {formatMoneyCents(result.reconciledCents)} of AR. Compare against the
								Accounts Receivable line on your prior system&apos;s trial balance to confirm nothing was missed.
							</Text>
						</View>
						{importErrors.length > 0 && (
							<View style={styles.errorsBox}>
								{importErrors.slice(0, 20).map((e, i) => <Text key={i} style={styles.errorLine}>{e}</Text>)}
								{importErrors.length > 20 && <Text style={styles.errorLine}>…and {importErrors.length - 20} more</Text>}
							</View>
						)}
						<Pressable
							style={styles.primary}
							onPress={() => { onClose(); router.push(`/associations/${associationId}/accounts`); }}
						>
							<Text style={styles.primaryLabel}>View Customer Accounts</Text>
						</Pressable>
						<Pressable style={styles.secondary} onPress={onClose}>
							<Text style={styles.secondaryLabel}>Close</Text>
						</Pressable>
					</>
				)}
			</ScrollView>
		</Modal>
	);
};

const PreviewRow: React.FC<{ label: string; value: string; tone?: 'red' | 'green' }> = ({ label, value, tone }) => (
	<View style={styles.previewRow}>
		<Text style={styles.previewLabel} numberOfLines={1}>{label}</Text>
		<Text style={[styles.previewCount, tone === 'red' && { color: '#c62828' }, tone === 'green' && { color: '#2e7d32' }]}>{value}</Text>
	</View>
);

const styles = StyleSheet.create({
	modal: { width: '92%', maxWidth: 620, maxHeight: '90%', borderRadius: 12, padding: 0 },
	header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#eee' },
	title: { fontSize: 18, fontWeight: '700' },
	close: { fontSize: 20, color: '#888', paddingHorizontal: 8 },
	body: { padding: 20, gap: 12 },
	hint: { color: '#546e7a', fontSize: 13, lineHeight: 19 },
	templatesBox: { backgroundColor: '#f5f7fa', borderRadius: 8, padding: 12, gap: 10 },
	templatesTitle: { fontSize: 11, fontWeight: '700', color: '#90a4ae', textTransform: 'uppercase', letterSpacing: 0.5 },
	templateRow: { flexDirection: 'row', gap: 8, alignItems: 'flex-start' },
	templateName: { color: '#1976d2', fontWeight: '600', fontSize: 13 },
	templateDesc: { color: '#78909c', fontSize: 11, lineHeight: 15, marginTop: 1 },
	primary: { backgroundColor: '#007AFF', padding: 12, borderRadius: 6, alignItems: 'center' },
	primaryLabel: { color: '#fff', fontWeight: '600' },
	secondary: { backgroundColor: '#eceff1', padding: 12, borderRadius: 6, alignItems: 'center', paddingHorizontal: 16 },
	secondaryLabel: { color: '#37474f', fontWeight: '500' },
	previewBox: { backgroundColor: '#f5f7fa', borderRadius: 8, padding: 12 },
	previewRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 4, gap: 12 },
	previewLabel: { color: '#455a64', flex: 1, minWidth: 0 },
	previewCount: { color: '#212121', fontWeight: '700' },
	previewDivider: { borderTopWidth: 1, borderTopColor: '#e0e6ea', marginVertical: 6 },
	note: { color: '#78909c', fontSize: 12, lineHeight: 17, marginTop: 6 },
	warnBox: { backgroundColor: '#fff8e1', borderLeftWidth: 3, borderLeftColor: '#fbc02d', borderRadius: 6, padding: 12, gap: 4 },
	warnTitle: { fontWeight: '700', color: '#795548', marginBottom: 4 },
	warnLine: { color: '#5d4037', fontSize: 12 },
	warnNote: { color: '#795548', fontSize: 12, marginTop: 6, fontStyle: 'italic' },
	resultBox: { padding: 12, backgroundColor: '#e8f5e9', borderRadius: 6, gap: 4 },
	resultOk: { color: '#2e7d32', fontWeight: '600' },
	resultLine: { color: '#37474f', fontSize: 13 },
	resultSkip: { color: '#ef6c00', fontWeight: '600' },
	errorsBox: { padding: 10, backgroundColor: '#fff3e0', borderRadius: 6, borderLeftWidth: 3, borderLeftColor: '#fbc02d' },
	errorLine: { color: '#795548', fontSize: 12, paddingVertical: 1 },
});
